import { Room } from '@/types/room';

/**
 * ルームデータをロードする
 * @returns ルーム一覧
 */
export async function loadRooms(searchParams?: URLSearchParams): Promise<Room[]> {
  try {
    // URLパラメータに基づいて読み込むJSONファイルを決定
    let jsonFile = '/data/free-rooms.json'; // デフォルトはフリーモード用

    if (searchParams) {
      const typeParam = searchParams.get('type');
      // ツアーモード（hostまたはguest）の場合はツアー用のJSONを読み込む
      if (typeParam === 'host' || typeParam === 'guest') {
        jsonFile = '/data/tour-rooms.json';
      }
    }

    console.log(`Loading rooms from: ${jsonFile}`);
    const response = await fetch(jsonFile);
    if (!response.ok) {
      throw new Error(`Failed to load rooms data from ${jsonFile}`);
    }

    let rooms: Room[] = await response.json();

    // エリアパラメータによるフィルタリング
    if (searchParams) {
      const areaParam = searchParams.get('area');
      if (areaParam) {
        const filteredRooms = rooms.filter((room) => room.area === areaParam);
        console.log(
          `Filtered rooms by area '${areaParam}':`,
          filteredRooms.map((r) => r.id)
        );
        rooms = filteredRooms;
      }
    }

    console.log(
      'Loaded rooms data with camera settings:',
      rooms.map((room) => ({
        id: room.id,
        defaultCamera: room.defaultCamera
      }))
    );
    return rooms;
  } catch (error) {
    console.error('Error loading rooms:', error);
    return [];
  }
}

/**
 * URLパラメータからルームIDを取得
 * @param searchParams URLのクエリパラメータ
 * @param rooms ルーム一覧
 * @returns 有効なルームID
 */
export function getInitialRoomId(searchParams: URLSearchParams, rooms: Room[]): string {
  // URLからルームIDを取得
  const roomId = searchParams.get('room');

  // ルームIDが指定されていて、有効な場合はそれを使用
  if (roomId && rooms.some((room) => room.id === roomId)) {
    return roomId;
  }

  // 指定がない場合や無効な場合は最初のルームを使用
  return rooms.length > 0 && rooms[0] ? rooms[0].id : '';
}

/**
 * 初期空間履歴IDを取得
 * @param room ルーム
 * @returns 空間履歴ID（最新のもの）
 */
export function getInitialHistoryId(room: Room | undefined): string {
  if (!room || room.histories.length === 0) {
    return '';
  }

  // 日付順にソートして最新の空間履歴を返す（降順 - 新しい順）
  const sortedHistories = [...room.histories].sort((a, b) => {
    // 日付が空の場合は最も古い日付として扱う
    if (!a.date) return 1;
    if (!b.date) return -1;

    // 日付文字列をDateオブジェクトに変換して比較
    const dateA = new Date(a.date.replace(/\//g, '-'));
    const dateB = new Date(b.date.replace(/\//g, '-'));

    return dateB.getTime() - dateA.getTime();
  });

  // ソート後の配列の最初の要素（最新の履歴）を返す
  const latestHistory = sortedHistories[0];
  return latestHistory ? latestHistory.id : '';
}
